import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowLeft, FileText, Info, Plus, Trash2,
} from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

/**
 * Document builder.
 *
 * The type choice is deliberately given real weight. A self-billed invoice, a
 * purchase order and a payment statement are legally different papers that
 * differ in who the document says it is from — so the picker states that in
 * words, and choosing self-billing without the vendor's agreement is refused
 * here as well as on the server.
 *
 * Nothing is numbered at this stage. A draft can be abandoned freely; issuing is
 * a separate, deliberate act on the document itself.
 */

const emptyLine = () => ({ description: "", quantity: "1", unit_price: "" });

function centsOf(value) {
  const text = String(value || "").trim().replace(/[$,]/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const [whole, frac = ""] = text.split(".");
  return Number(whole) * 100 + Number(frac.padEnd(2, "0"));
}

export default function DocumentBuilderPage() {
  const navigate = useNavigate();
  const { authAxios } = useAuth();

  const [types, setTypes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [docType, setDocType] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [taxPercent, setTaxPercent] = useState("0");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    try {
      const api = authAxios();
      const [t, v] = await Promise.all([
        api.get("/document-types"),
        api.get("/vendors"),
      ]);
      setTypes(t.data.types);
      setDocType(t.data.default);
      setVendors(v.data);
    } catch {
      toast.error("Could not load the builder");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const vendor = vendors.find((v) => v.vendor_id === vendorId);
  const selectedType = types.find((t) => t.id === docType);

  // Self-billing without the supplier's agreement is not a formatting choice,
  // so it is blocked here as well as on the server — and explained, rather than
  // simply refused at submit time.
  const blockedBySelfBilling =
    docType === "self_billed_invoice" && vendor && !vendor.self_billing_agreed;

  const totals = useMemo(() => {
    let subtotal = 0;
    let invalid = false;
    for (const line of lines) {
      if (!line.description.trim() && !line.unit_price) continue;
      const cents = centsOf(line.unit_price);
      const qty = Number(line.quantity);
      if (cents === null || !Number.isFinite(qty) || qty <= 0) {
        invalid = true;
        continue;
      }
      subtotal += Math.round(cents * qty);
    }
    const pct = Number(taxPercent) || 0;
    const tax = Math.round(subtotal * pct / 100);
    return { subtotal, tax, total: subtotal + tax, invalid };
  }, [lines, taxPercent]);

  const money = (cents) => `$${(cents / 100).toFixed(2)}`;

  const updateLine = (index, field, value) =>
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, [field]: value } : l)));

  const save = async () => {
    setError("");
    if (!docType) return setError("Choose a document type.");
    if (!vendorId) return setError("Choose a vendor.");
    if (blockedBySelfBilling) {
      return setError(
        `${vendor.name} has not agreed to self-billing. Record the agreement on their `
        + "business details, or raise a purchase order instead."
      );
    }
    const filled = lines.filter((l) => l.description.trim() && l.unit_price);
    if (!filled.length) return setError("Add at least one line with a description and price.");
    if (totals.invalid) return setError("Check the amounts — use digits with at most two decimals.");

    setSaving(true);
    try {
      const api = authAxios();
      const res = await api.post("/documents", {
        doc_type: docType,
        vendor_id: vendorId,
        lines: filled.map((l) => ({
          description: l.description.trim(),
          quantity: Number(l.quantity) || 1,
          unit_price: String(l.unit_price).trim(),
        })),
        tax_percent: String(taxPercent || "0"),
        due_date: dueDate,
        notes,
      });
      toast.success("Draft created");
      navigate(`/documents/${res.data.document_id}`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not create the document");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8 w-full space-y-3">
        <div className="h-20 bg-muted rounded-xl animate-pulse" />
        <div className="h-72 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 w-full space-y-5 pb-24">
      <Button variant="ghost" size="sm" className="-ml-2 gap-1.5"
              onClick={() => navigate("/documents")} data-testid="builder-back">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Documents
      </Button>

      <div>
        <h1 className="text-2xl font-bold text-foreground">New document</h1>
        <p className="text-sm text-muted-foreground">
          Creates a draft. Nothing is numbered until you issue it.
        </p>
      </div>

      {/* The type is not a template choice, so it is presented as a decision
          with consequences spelled out. */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <Label>What kind of document is this?</Label>
          <div className="space-y-2">
            {types.map((t) => (
              <button key={t.id} type="button" onClick={() => setDocType(t.id)}
                      aria-pressed={docType === t.id}
                      className={`w-full text-left rounded-lg border p-4 transition-colors ${
                        docType === t.id
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/40"}`}
                      data-testid={`type-${t.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-foreground">{t.title}</span>
                  <span className="font-mono text-xs text-muted-foreground">{t.prefix}-</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Issued by <strong className="text-foreground">{t.issued_by}</strong>
                </p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{t.note}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="vendor">Vendor</Label>
            <select id="vendor" value={vendorId}
                    onChange={(e) => setVendorId(e.target.value)}
                    className="w-full h-11 rounded-lg border border-border bg-card
                               text-foreground px-3 text-sm"
                    data-testid="builder-vendor">
              <option value="">Choose a vendor…</option>
              {vendors.map((v) => (
                <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>
              ))}
            </select>
          </div>

          {blockedBySelfBilling && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/40
                            bg-amber-500/10 p-3" data-testid="self-billing-warning">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0"
                             aria-hidden="true" />
              <p className="text-xs text-amber-700 dark:text-amber-500 leading-relaxed">
                <strong>{vendor.name} has not agreed to self-billing.</strong> Raising an
                invoice in a supplier's name without their agreement is not a formatting
                choice. Record the agreement on their business details, or raise a purchase
                order instead.
              </p>
            </div>
          )}

          {vendor && !vendor.legal_name && (
            <div className="flex items-start gap-2 rounded-lg bg-muted p-3"
                 data-testid="missing-business-details">
              <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0"
                    aria-hidden="true" />
              <p className="text-xs text-muted-foreground leading-relaxed">
                {vendor.name} has no legal name, address or tax id recorded, so the document
                will show only their display name.{" "}
                <button type="button" className="text-primary underline"
                        onClick={() => navigate(`/documents/vendor/${vendor.vendor_id}`)}
                        data-testid="goto-business-details">
                  Add business details
                </button>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <Label>Lines</Label>
            <Button size="sm" variant="outline" className="h-10 gap-1.5"
                    onClick={() => setLines((p) => [...p, emptyLine()])}
                    data-testid="add-line">
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add line
            </Button>
          </div>

          {lines.map((line, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end"
                 data-testid={`line-${i}`}>
              <div className="col-span-12 sm:col-span-6 space-y-1.5">
                <Label htmlFor={`desc-${i}`} className="text-xs">Description</Label>
                <Input id={`desc-${i}`} className="h-11" value={line.description}
                       onChange={(e) => updateLine(i, "description", e.target.value)}
                       data-testid={`line-desc-${i}`} />
              </div>
              <div className="col-span-4 sm:col-span-2 space-y-1.5">
                <Label htmlFor={`qty-${i}`} className="text-xs">Qty</Label>
                <Input id={`qty-${i}`} className="h-11" inputMode="decimal"
                       value={line.quantity}
                       onChange={(e) => updateLine(i, "quantity", e.target.value)}
                       data-testid={`line-qty-${i}`} />
              </div>
              <div className="col-span-5 sm:col-span-3 space-y-1.5">
                <Label htmlFor={`price-${i}`} className="text-xs">Unit price</Label>
                <Input id={`price-${i}`} className="h-11 font-mono" inputMode="decimal"
                       placeholder="0.00" value={line.unit_price}
                       onChange={(e) => updateLine(i, "unit_price", e.target.value)}
                       data-testid={`line-price-${i}`} />
              </div>
              <div className="col-span-3 sm:col-span-1">
                <Button variant="ghost" size="sm" className="h-11 w-full"
                        aria-label={`Remove line ${i + 1}`}
                        disabled={lines.length === 1}
                        onClick={() => setLines((p) => p.filter((_, j) => j !== i))}
                        data-testid={`remove-line-${i}`}>
                  <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="tax">Tax %</Label>
              <Input id="tax" className="h-11" inputMode="decimal" value={taxPercent}
                     onChange={(e) => setTaxPercent(e.target.value)}
                     data-testid="builder-tax" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="due">Due date</Label>
              <Input id="due" type="date" className="h-11" value={dueDate}
                     onChange={(e) => setDueDate(e.target.value)}
                     data-testid="builder-due" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="notes">Notes on the document</Label>
            <Textarea id="notes" rows={2} value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      data-testid="builder-notes" />
          </div>

          <div className="border-t border-border pt-3 space-y-1 text-sm"
               data-testid="builder-totals">
            <div className="flex justify-between text-muted-foreground">
              <span>Subtotal</span><span className="font-mono">{money(totals.subtotal)}</span>
            </div>
            {totals.tax > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span>Tax</span><span className="font-mono">{money(totals.tax)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-foreground pt-1">
              <span>Total</span>
              <span className="font-mono" data-testid="builder-total">{money(totals.total)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <p className="text-sm text-destructive flex items-start gap-1.5"
           data-testid="builder-error">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />{error}
        </p>
      )}

      <Button className="w-full h-12 gap-1.5" onClick={save}
              disabled={saving || blockedBySelfBilling} data-testid="builder-save">
        <FileText className="h-4 w-4" aria-hidden="true" />
        {saving ? "Creating…" : `Create ${selectedType?.title.toLowerCase() || "document"} draft`}
      </Button>
    </div>
  );
}
